import { isDefined } from "@clipboard-health/util-ts";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useShiftInviteActions } from "@src/appV2/redesign/WorkerShifts/ShiftInvites/api/useShiftInviteActions";
import { useShiftInviteItemContext } from "@src/appV2/redesign/WorkerShifts/ShiftInvites/context/shiftInviteItemContext";
import { generatePath, useHistory, useLocation } from "react-router-dom";

import { LocationPermissionsBottomSheet } from "../../Shift/Bookability/LocationPermissionsBottomSheet";
import { MY_SHIFTS_FULL_PATH, SHIFT_INVITES_FULL_PATH } from "../path";
import { ShiftInviteArrivalTimeBottomSheetContainer } from "./ArrivalTime/ArrivalTimeBottomSheetContainer";
import { ShiftInviteConfirmShiftBookingBottomSheetContainer } from "./ArrivalTime/ConfirmBookingBottomSheetContainer";
import { ShiftInviteBookedBottomSheetContainer } from "./ArrivalTime/ShiftBookedBottomSheetContainer";
import { MissingCoreRequiredDocumentsBottomSheet } from "./dialogs/MissingCoreRequiredDocumentsBottomSheet";
import { MissingFacilityRequiredDocumentsBottomSheet } from "./dialogs/MissingFacilityRequiredDocumentsBottomSheet";
import { OverworkingBottomSheet } from "./dialogs/OverworkingBottomSheet";
import { ShiftBookingConflictBottomSheet } from "./dialogs/ShiftBookingConflictBottomSheet";
import { ShiftInviteConflictBottomSheet } from "./dialogs/ShiftInviteConflictBottomSheet";
import { WeeklyHoursLimitExceededBottomSheet } from "./dialogs/WeeklyHoursLimitExceededBottomSheet";
import { useShiftInviteSnapshot } from "./hooks/useShiftInviteSnapshot";
import { SHIFT_INVITE_BOOKED_CONFIRMATION_FULL_PATH } from "./paths";
import { type ShiftInvite } from "./types";

/*
  The different shift invite modals should be migrated to the Shift Invite Modal Routes
*/
interface ShiftInviteAlertsProps {
  shiftInvite: ShiftInvite;
}
export function ShiftInviteAlerts(props: ShiftInviteAlertsProps) {
  const { shiftInvite } = props;
  const history = useHistory();
  const location = useLocation();
  const { acceptShiftInvite, isLoadingAcceptOrDecline } = useShiftInviteActions();
  const {
    conflictingShift,
    conflictingInvites,
    conflictingShiftDialogState,
    conflictingInviteDialogState,
    overworkingShiftDialogState,
    locationPermissionsModalState,
    arrivalTimeModalState,
    confirmShiftBookingModalState,
    shiftBookedConfirmationModalState,
    missingFacilityRequiredDocumentsDialogState,
    missingCoreRequiredDocumentsDialogState,
    missingDocuments,
    weeklyHoursLimitExceededDialogState,
    weeklyHoursLimit,
    resetWarnings,
    shouldDeclineConflictingInvites,
    skipFacilityRequiredDocumentCheck,
    isOverworkingShiftsAllowed,
    setShouldDeclineConflictingInvites,
    setSkipFacilityRequiredDocumentCheck,
    setIsOverworkingShiftsAllowed,
  } = useShiftInviteItemContext();
  const { setShiftInviteSnapshot } = useShiftInviteSnapshot(shiftInvite.id);

  return (
    <>
      {isDefined(conflictingShift) && (
        <ShiftBookingConflictBottomSheet
          modalState={conflictingShiftDialogState}
          conflictingShift={conflictingShift}
          onClose={() => {
            resetWarnings();
            conflictingShiftDialogState.closeModal();
          }}
          onConfirm={() => {
            resetWarnings();
            conflictingShiftDialogState.closeModal();
            history.push(MY_SHIFTS_FULL_PATH);
          }}
        />
      )}

      {conflictingInvites?.length > 0 && (
        <ShiftInviteConflictBottomSheet
          modalState={conflictingInviteDialogState}
          conflictingInvites={conflictingInvites}
          onCancel={() => {
            resetWarnings();
            conflictingInviteDialogState.closeModal();
          }}
          onConfirm={async () => {
            conflictingInviteDialogState.closeModal();
            setShouldDeclineConflictingInvites(true);
            await acceptShiftInvite({
              shiftInvite,
              isOverworkingShiftsAllowed,
              skipFacilityRequiredDocumentCheck,
              shouldDeclineConflictingInvites: true,
            });
          }}
        />
      )}

      <OverworkingBottomSheet
        modalState={overworkingShiftDialogState}
        restrictOverworkShiftInvites={shiftInvite.attributes.facility.restrictOverworkShiftInvites}
        onCancel={() => {
          resetWarnings();
          overworkingShiftDialogState.closeModal();
        }}
        onConfirm={async () => {
          overworkingShiftDialogState.closeModal();
          setIsOverworkingShiftsAllowed(true);
          await acceptShiftInvite({
            shiftInvite,
            isOverworkingShiftsAllowed: true,
            shouldDeclineConflictingInvites,
            skipFacilityRequiredDocumentCheck,
          });
          logEvent(APP_V2_APP_EVENTS.ACCEPT_OVERWORKING_SHIFT_INVITE, {
            shiftInvite,
          });
        }}
      />

      <MissingFacilityRequiredDocumentsBottomSheet
        modalState={missingFacilityRequiredDocumentsDialogState}
        missingDocuments={missingDocuments}
        workplaceName={shiftInvite.attributes.facility.name}
        onCancel={() => {
          resetWarnings();
          missingFacilityRequiredDocumentsDialogState.closeModal();
        }}
        onConfirm={async () => {
          missingFacilityRequiredDocumentsDialogState.closeModal();
          setSkipFacilityRequiredDocumentCheck(true);
          await acceptShiftInvite({
            shiftInvite,
            shouldDeclineConflictingInvites,
            isOverworkingShiftsAllowed,
            skipFacilityRequiredDocumentCheck: true,
          });
        }}
      />

      <MissingCoreRequiredDocumentsBottomSheet
        modalState={missingCoreRequiredDocumentsDialogState}
        missingDocuments={missingDocuments}
        onCancel={() => {
          resetWarnings();
          missingCoreRequiredDocumentsDialogState.closeModal();
        }}
      />

      {weeklyHoursLimit > 0 && (
        <WeeklyHoursLimitExceededBottomSheet
          modalState={weeklyHoursLimitExceededDialogState}
          weeklyHoursLimit={weeklyHoursLimit}
          onConfirm={() => {
            resetWarnings();
            weeklyHoursLimitExceededDialogState.closeModal();
            history.push(MY_SHIFTS_FULL_PATH);
          }}
          onClose={() => {
            resetWarnings();
            weeklyHoursLimitExceededDialogState.closeModal();
          }}
        />
      )}

      <LocationPermissionsBottomSheet modalState={locationPermissionsModalState} />

      <ShiftInviteConfirmShiftBookingBottomSheetContainer
        modalState={confirmShiftBookingModalState}
        shiftInvite={shiftInvite}
        isConfirming={isLoadingAcceptOrDecline}
        onConfirm={async (computedArrivalData) => {
          // Persisting the shift invite snapshot in React Query cache as it gets
          // removed from API response after we accept/decline the shift invite,
          // and we'll need that data for the booked confirmation modal
          setShiftInviteSnapshot({
            shiftInvite,
            computedArrivalData,
          });

          const result = await acceptShiftInvite({
            shiftInvite,
            computedArrivalData,
            isShortLeadTime: true,
          });

          confirmShiftBookingModalState.closeModal();

          // The accept shift invite function returns false if not successful, this may open other modals
          if (!result) {
            return;
          }

          if (location.pathname === SHIFT_INVITES_FULL_PATH) {
            // Use shift invite modal route if accepting invite from shift invites page
            history.push(
              generatePath(SHIFT_INVITE_BOOKED_CONFIRMATION_FULL_PATH, {
                shiftInviteId: shiftInvite.id,
              })
            );
          } else {
            shiftBookedConfirmationModalState.openModal();
          }
        }}
      />

      <ShiftInviteArrivalTimeBottomSheetContainer
        modalState={arrivalTimeModalState}
        shiftInvite={shiftInvite}
        onConfirm={async () => {
          arrivalTimeModalState.closeModal();
          confirmShiftBookingModalState.openModal();
        }}
      />

      <ShiftInviteBookedBottomSheetContainer
        modalState={shiftBookedConfirmationModalState}
        shiftInvite={shiftInvite}
      />
    </>
  );
}
